import { useState } from 'react';
import { SuccessStory } from '../lib/types';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { Heart, CheckCircle, XCircle, Eye, Loader2, Trash2, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const PLACEHOLDER = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Crect width="100" height="100" fill="%23f3f4f6"/%3E%3Ccircle cx="50" cy="38" r="18" fill="%23d1d5db"/%3E%3Cellipse cx="50" cy="85" rx="30" ry="20" fill="%23d1d5db"/%3E%3C/svg%3E';

interface SuccessStoriesModerationProps {
  stories: SuccessStory[];
  onApproveStory: (storyId: string) => Promise<void>;
  onRejectStory: (storyId: string) => Promise<void>;
  onDeleteStory: (storyId: string) => Promise<void>;
}

function CoupleAvatars({ story }: { story: SuccessStory }) {
  const p1 = story.profile1;
  const p2 = story.profile2;
  const sameId = (p1 as any)?._id === (p2 as any)?._id && (p1 as any)?._id !== 'unknown';
  const profiles = sameId ? [p1] : [p1, p2].filter(p => p?.name && p.name !== 'Unknown');

  return (
    <div className="flex -space-x-2">
      {profiles.map((p, i) => (
        p?.profilePicture ? (
          <img key={i} src={p.profilePicture} alt={p.name}
            className="size-9 rounded-full object-cover border-2 border-white"
            onError={e => { (e.target as HTMLImageElement).src = PLACEHOLDER; }} />
        ) : (
          <div key={i} className="size-9 rounded-full bg-gradient-to-br from-pink-400 to-purple-500 border-2 border-white flex items-center justify-center text-white text-sm font-bold">
            {p?.name?.[0]?.toUpperCase() || '?'}
          </div>
        )
      ))}
    </div>
  );
}

function CoupleName({ story }: { story: SuccessStory }) {
  const p1 = story.profile1;
  const p2 = story.profile2;
  const n1 = p1?.name && p1.name !== 'Unknown' ? p1.name : null;
  const n2 = p2?.name && p2.name !== 'Unknown' ? p2.name : null;
  const sameId = (p1 as any)?._id === (p2 as any)?._id && (p1 as any)?._id !== 'unknown';

  if (!n1 && !n2) return <span>Couple's Story</span>;
  if (sameId || n1 === n2) return <span>{n1}</span>;
  if (!n2) return <span>{n1}</span>;
  if (!n1) return <span>{n2}</span>;
  return <span>{n1} &amp; {n2}</span>;
}

export function SuccessStoriesModeration({
  stories,
  onApproveStory,
  onRejectStory,
  onDeleteStory,
}: SuccessStoriesModerationProps) {
  const [selected, setSelected] = useState<SuccessStory | null>(null);
  const [busy, setBusy] = useState<'approve' | 'reject' | 'delete' | null>(null);
  const [storyToDelete, setStoryToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const pending = stories.filter(s => !s.approved);
  const approved = stories.filter(s => s.approved);

  const handle = async (type: 'approve' | 'reject') => {
    if (!selected) return;
    setBusy(type);
    try {
      if (type === 'approve') {
        await onApproveStory(selected.id);
        toast.success('Story approved and published!');
      } else {
        await onRejectStory(selected.id);
        toast.success('Story rejected');
      }
      setSelected(null);
    } finally { setBusy(null); }
  };

  const handleDelete = async () => {
    if (!storyToDelete) return;
    setDeleting(true);
    try {
      await onDeleteStory(storyToDelete);
      toast.success('Story deleted');
      setStoryToDelete(null);
      if (selected?.id === storyToDelete) setSelected(null);
    } finally { setDeleting(false); }
  };

  const StoryCard = ({ story }: { story: SuccessStory }) => {
    const isPending = !story.approved;
    return (
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden hover:shadow-md transition-shadow">
        {/* Cover image */}
        <div className="relative h-40 bg-gradient-to-br from-pink-100 to-purple-100">
          {story.images[0] ? (
            <img src={story.images[0]} alt="Wedding"
              className="w-full h-full object-cover"
              onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <Heart className="size-10 text-pink-300 fill-pink-200" />
            </div>
          )}
          <span className={`absolute top-2 right-2 text-[10px] font-semibold px-2 py-1 rounded-full ${
            isPending ? 'bg-orange-500 text-white' : 'bg-emerald-500 text-white'
          }`}>
            {isPending ? 'Pending' : 'Published'}
          </span>
        </div>

        {/* Info */}
        <div className="p-3.5">
          <div className="flex items-center gap-2.5 mb-2">
            <CoupleAvatars story={story} />
            <div className="min-w-0">
              <p className="font-semibold text-sm text-gray-900 truncate"><CoupleName story={story} /></p>
              <p className="text-xs text-gray-400 flex items-center gap-1">
                <Clock className="size-3" />
                {format(story.weddingDate, 'MMM d, yyyy')}
              </p>
            </div>
          </div>
          {story.story && (
            <p className="text-xs text-gray-500 line-clamp-2 mb-3">{story.story}</p>
          )}

          {/* Actions */}
          <div className="flex gap-1.5">
            <button onClick={() => setSelected(story)}
              className="flex-1 h-7 text-xs font-semibold bg-violet-50 hover:bg-violet-100 text-violet-700 rounded-lg flex items-center justify-center gap-1 transition-colors">
              <Eye className="size-3" /> {isPending ? 'Review' : 'View'}
            </button>
            <button onClick={() => setStoryToDelete(story.id)}
              className="h-7 w-7 flex items-center justify-center rounded-lg bg-red-50 hover:bg-red-100 text-red-500 transition-colors">
              <Trash2 className="size-3.5" />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Pending */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="size-8 rounded-xl bg-orange-500 flex items-center justify-center">
            <Clock className="size-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Pending Review</h3>
            <p className="text-xs text-gray-400">{pending.length} awaiting approval</p>
          </div>
        </div>
        {pending.length === 0 ? (
          <div className="text-center py-10 text-gray-400 bg-gray-50 rounded-2xl">
            <CheckCircle className="size-10 mx-auto mb-2 text-gray-300" />
            <p className="text-sm font-medium text-gray-500">All caught up!</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {pending.map(s => <StoryCard key={s.id} story={s} />)}
          </div>
        )}
      </div>

      {/* Published */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <div className="size-8 rounded-xl bg-emerald-500 flex items-center justify-center">
            <CheckCircle className="size-4 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Published Stories</h3>
            <p className="text-xs text-gray-400">{approved.length} live</p>
          </div>
        </div>
        {approved.length === 0 ? (
          <div className="text-center py-8 text-gray-400 bg-gray-50 rounded-2xl">
            <p className="text-sm">No published stories yet</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {approved.map(s => <StoryCard key={s.id} story={s} />)}
          </div>
        )}
      </div>

      {/* Detail / Review Dialog */}
      <Dialog open={!!selected} onOpenChange={v => { if (!busy) { if (!v) setSelected(null); } }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Heart className="size-4 text-pink-500 fill-pink-400" />
                  {selected.approved ? 'Story Details' : 'Review Story'}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                {/* Images */}
                {selected.images.length > 0 && (
                  <div className={`grid gap-2 ${selected.images.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {selected.images.map((img, i) => (
                      <img key={i} src={img} alt="" className="w-full h-44 object-cover rounded-xl"
                        onError={e => { (e.target as HTMLImageElement).src = PLACEHOLDER; }} />
                    ))}
                  </div>
                )}

                {/* Couple info */}
                <div className="flex items-center gap-3 p-4 bg-pink-50 rounded-xl border border-pink-100">
                  <CoupleAvatars story={selected} />
                  <div>
                    <p className="font-semibold text-gray-900"><CoupleName story={selected} /></p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      Wedding: {format(selected.weddingDate, 'MMMM d, yyyy')}
                    </p>
                  </div>
                </div>

                {/* Story text */}
                {selected.story && (
                  <div className="p-4 bg-gray-50 rounded-xl">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Their Story</p>
                    <p className="text-sm text-gray-700 leading-relaxed">{selected.story}</p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  {!selected.approved ? (
                    <>
                      <button onClick={() => handle('reject')} disabled={!!busy}
                        className="flex-1 h-9 text-sm font-semibold border border-red-200 text-red-600 hover:bg-red-50 rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-50 transition-colors">
                        {busy === 'reject' ? <Loader2 className="size-4 animate-spin" /> : <XCircle className="size-4" />}
                        Reject
                      </button>
                      <button onClick={() => handle('approve')} disabled={!!busy}
                        className="flex-1 h-9 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl flex items-center justify-center gap-1.5 disabled:opacity-50 transition-colors">
                        {busy === 'approve' ? <Loader2 className="size-4 animate-spin" /> : <CheckCircle className="size-4" />}
                        Approve &amp; Publish
                      </button>
                    </>
                  ) : (
                    <div className="flex-1 flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 px-3 py-2 rounded-xl">
                      <CheckCircle className="size-4 shrink-0" /> Published and visible to users
                    </div>
                  )}
                  <button onClick={() => { setStoryToDelete(selected.id); setSelected(null); }}
                    className="h-9 px-3 flex items-center justify-center rounded-xl bg-red-50 hover:bg-red-100 text-red-500 transition-colors">
                    <Trash2 className="size-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!storyToDelete} onOpenChange={() => { if (!deleting) setStoryToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Story?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this success story. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700" disabled={deleting}>
              {deleting ? <><Loader2 className="size-4 mr-1 animate-spin" />Deleting…</> : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
